import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, formatBRL, formatBRLc, formatKM } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Printer, QrCode, Eye, Trash2, Loader2, CheckCircle2, Sparkles, Pencil, ScanLine, BadgeCheck, RotateCcw, TrendingUp, Clock, Star, Rocket, CreditCard, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

export default function MeusAnuncios() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [scansOpen, setScansOpen] = useState(false);
  const [scans, setScans] = useState(null);
  const [scansCar, setScansCar] = useState(null);
  const [soldOpen, setSoldOpen] = useState(false);
  const [soldCar, setSoldCar] = useState(null);
  const [keepVisible, setKeepVisible] = useState(true);
  const [destaqueOpen, setDestaqueOpen] = useState(false);
  const [destaqueCar, setDestaqueCar] = useState(null);

  const DESTAQUE_PLANS = [
    { plan: 3, price: 15, label: "3 dias", every: "a cada 3 dias", tag: "" },
    { plan: 7, price: 20, label: "7 dias", every: "por semana", tag: "Mais popular" },
    { plan: 15, price: 35, label: "15 dias", every: "a cada 15 dias", tag: "Melhor valor" },
  ];

  const cancelDestaque = async (car) => {
    try {
      await api.post(`/listings/${car.id}/cancel-destaque`);
      toast.success("Destaque cancelado. Fica no topo até o fim do período já pago.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao cancelar destaque"); }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.origin + "/painel" });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao abrir o portal"); }
  };

  const load = () => {
    setLoading(true);
    api.get("/listings/mine").then(({ data }) => setCars(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openScans = async (car) => {
    setScansCar(car); setScans(null); setScansOpen(true);
    try { const { data } = await api.get(`/listings/${car.id}/scans`); setScans(data); }
    catch { setScans({ count: 0, recent: [] }); }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const gerarCartaz = async (id) => {
    setPayingId(id + ":cartaz");
    try {
      const { data } = await api.post("/payments/checkout", { listing_id: id, origin_url: window.location.origin, kind: "cartaz" });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao iniciar pagamento");
      setPayingId(null);
    }
  };

  const destacarTopo = async (id, plan) => {
    setPayingId(id + ":destaque");
    try {
      const { data } = await api.post("/payments/checkout", { listing_id: id, origin_url: window.location.origin, kind: "destaque", plan });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao iniciar pagamento");
      setPayingId(null);
    }
  };

  const remover = async (id) => {
    try { await api.delete(`/listings/${id}`); toast.success("Anúncio removido"); load(); }
    catch { toast.error("Erro ao remover"); }
  };

  const setStatus = async (car, status, showWhenSold) => {
    try {
      await api.patch(`/listings/${car.id}/status`, { status, show_when_sold: !!showWhenSold });
      if (status === "sold") toast.success(showWhenSold ? "Marcado como VENDIDO e mantido na vitrine 🎉" : "Marcado como VENDIDO! 🎉");
      else toast.success("Anúncio reativado");
      setSoldOpen(false);
      load();
    } catch { toast.error("Erro ao atualizar status"); }
  };

  const toggleVisible = async (car, val) => {
    try { await api.patch(`/listings/${car.id}/status`, { status: "sold", show_when_sold: val }); load(); }
    catch { toast.error("Erro ao atualizar"); }
  };

  const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }); } catch { return ""; } };

  const totalViews = cars.reduce((a, c) => a + (c.views || 0), 0);
  const totalAiClicks = cars.reduce((a, c) => a + (c.ai_clicks || 0), 0);
  const pagos = cars.filter((c) => c.poster_paid).length;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="painel-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline text-blaze mb-2">Painel do vendedor</p>
          <h1 className="font-heading font-800 text-3xl md:text-4xl">Olá, {user?.name?.split(" ")[0]} 👋</h1>
        </div>
        <div className="flex items-center gap-2">
          {cars.some((c) => c.featured || c.featured_subscription_id || c.has_cartaz_sub) && (
            <Button variant="outline" className="rounded-sm border-obsidian/15 font-600" onClick={openPortal} data-testid="portal-btn">
              <CreditCard className="w-4 h-4 mr-2" /> Gerenciar assinatura
            </Button>
          )}
          <Button className="bg-obsidian hover:bg-blaze text-white font-700 rounded-sm" onClick={() => navigate("/anunciar")} data-testid="novo-anuncio-btn">
            <Plus className="w-4 h-4 mr-2" /> Novo Anúncio
          </Button>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
        {[
          { label: "Anúncios", value: cars.length },
          { label: "Scans do QR", value: totalViews },
          { label: "Cliques da IA", value: totalAiClicks },
          { label: "Materiais liberados", value: pagos },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-border rounded-sm p-5" data-testid={`stat-${i}`}>
            <p className="font-heading font-900 text-3xl text-obsidian">{s.value}</p>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blaze" /></div>
      ) : cars.length === 0 ? (
        <div className="bg-white border border-border rounded-sm p-16 text-center">
          <p className="text-muted-foreground">Você ainda não tem anúncios.</p>
          <Button className="mt-4 bg-obsidian hover:bg-blaze text-white rounded-sm" onClick={() => navigate("/anunciar")} data-testid="empty-novo-btn">Criar meu primeiro anúncio</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {cars.map((c) => (
            <div key={c.id} className="bg-white border border-border rounded-sm p-4 flex flex-col md:flex-row gap-5" data-testid={`mine-card-${c.id}`}>
              <div className="w-full md:w-40 aspect-[4/3] rounded-sm overflow-hidden bg-secondary shrink-0 relative">
                <img src={c.images?.[0] ? fileUrl(c.images[0]) : "https://images.unsplash.com/photo-1630356220662-4d9804d3a987"} alt="" className={`w-full h-full object-cover ${c.status === "sold" ? "grayscale opacity-60" : ""}`} />
                {c.status === "sold" && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="bg-blaze text-white font-poster text-xl px-3 py-1 -rotate-6 shadow-lg" data-testid={`sold-badge-${c.id}`}>VENDIDO</span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-700 text-lg">{c.marca} {c.modelo}</p>
                    <p className="font-heading font-900 text-xl text-obsidian mt-1">{formatBRL(c.preco)}</p>
                    <p className="text-sm text-muted-foreground mt-1">{c.ano} · {formatKM(c.km)} · {c.views || 0} scans do QR</p>
                    {c.ai_clicks > 0 && (
                      <span className="mt-2 inline-flex items-center gap-1 text-xs font-700 bg-blaze/10 text-blaze px-2 py-1 rounded-sm" data-testid={`ai-clicks-${c.id}`}>
                        <Sparkles className="w-3.5 h-3.5" /> {c.ai_clicks} clique(s) via Assistente de IA
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    {c.featured && (
                      <span className="inline-flex items-center gap-1 text-xs font-800 bg-blaze text-white px-2 py-1 rounded-sm" data-testid={`featured-tag-${c.id}`}><Star className="w-3.5 h-3.5 fill-white" /> Destaque até {fmtDay(c.featured_until)}</span>
                    )}
                    {c.status === "sold" && (
                      <span className="inline-flex items-center gap-1 text-xs font-700 bg-blaze/10 text-blaze px-2 py-1 rounded-sm"><BadgeCheck className="w-3.5 h-3.5" /> Vendido</span>
                    )}
                    {c.poster_paid ? (
                      c.qr_active ? (
                        <span className="inline-flex items-center gap-1 text-xs font-700 bg-green-100 text-green-700 px-2 py-1 rounded-sm" data-testid={`qr-status-${c.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> QR ativo · assinatura</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-700 bg-blaze/10 text-blaze px-2 py-1 rounded-sm" data-testid={`qr-status-${c.id}`}><Clock className="w-3.5 h-3.5" /> QR desativado</span>
                      )
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-700 bg-secondary text-muted-foreground px-2 py-1 rounded-sm">Sem Kit de Divulgação</span>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="rounded-sm border-obsidian/15" onClick={() => navigate(`/anuncio/${c.id}?token=${c.access_token}`)} data-testid={`preview-${c.id}`}>
                    <Eye className="w-4 h-4 mr-1" /> Ver anúncio
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm border-obsidian/15" onClick={() => navigate(`/editar/${c.id}`)} data-testid={`editar-${c.id}`}>
                    <Pencil className="w-4 h-4 mr-1" /> Editar
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm border-obsidian/15" onClick={() => openScans(c)} data-testid={`scans-${c.id}`}>
                    <ScanLine className="w-4 h-4 mr-1" /> Scans ({c.views || 0})
                  </Button>
                  {c.status === "sold" ? (
                    <Button size="sm" variant="outline" className="rounded-sm border-obsidian/15" onClick={() => setStatus(c, "active")} data-testid={`reativar-${c.id}`}>
                      <RotateCcw className="w-4 h-4 mr-1" /> Reativar
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="rounded-sm border-green-600/40 text-green-700 hover:bg-green-600 hover:text-white" onClick={() => { setSoldCar(c); setKeepVisible(true); setSoldOpen(true); }} data-testid={`vendido-${c.id}`}>
                      <BadgeCheck className="w-4 h-4 mr-1" /> Marcar VENDIDO
                    </Button>
                  )}
                  {c.status !== "sold" && (
                    c.featured ? (
                      c.featured_canceling ? (
                        <span className="inline-flex items-center gap-1 text-xs font-700 bg-secondary text-muted-foreground px-3 rounded-sm h-8" data-testid={`featured-canceling-${c.id}`}><Clock className="w-3.5 h-3.5" /> Ativo até {fmtDay(c.featured_until)}</span>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-sm border-obsidian/15" onClick={() => cancelDestaque(c)} data-testid={`cancel-destaque-${c.id}`}>
                          <Star className="w-3.5 h-3.5 mr-1 fill-blaze text-blaze" /> No topo · Cancelar
                        </Button>
                      )
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-sm border-blaze/40 text-blaze hover:bg-blaze hover:text-white" disabled={payingId === c.id + ":destaque"} onClick={() => { setDestaqueCar(c); setDestaqueOpen(true); }} data-testid={`destaque-${c.id}`}>
                        {payingId === c.id + ":destaque" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Rocket className="w-4 h-4 mr-1" />}
                        Destacar no topo
                      </Button>
                    )
                  )}
                  {c.poster_paid && c.qr_active ? (
                    <>
                      <Button size="sm" className="rounded-sm bg-blaze hover:bg-obsidian text-white" onClick={() => navigate(`/cartaz/${c.id}`)} data-testid={`cartaz-${c.id}`}>
                        <Printer className="w-4 h-4 mr-1" /> Imprimir materiais
                      </Button>
                      {c.has_cartaz_sub && (
                        <Button size="sm" variant="outline" className="rounded-sm border-obsidian/15" onClick={openPortal} data-testid={`manage-cartaz-${c.id}`}>
                          <CreditCard className="w-4 h-4 mr-1" /> Gerenciar assinatura
                        </Button>
                      )}
                    </>
                  ) : c.poster_paid ? (
                    <Button size="sm" className="rounded-sm bg-blaze hover:bg-obsidian text-white" disabled={payingId === c.id + ":cartaz"} onClick={() => gerarCartaz(c.id)} data-testid={`reativar-cartaz-${c.id}`}>
                      {payingId === c.id + ":cartaz" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                      Reativar assinatura · {formatBRLc(29)}/tri
                    </Button>
                  ) : (
                    <Button size="sm" className="rounded-sm bg-obsidian hover:bg-blaze text-white" disabled={payingId === c.id + ":cartaz"} onClick={() => gerarCartaz(c.id)} data-testid={`gerar-cartaz-${c.id}`}>
                      {payingId === c.id + ":cartaz" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                      Assinar Kit de Divulgação · {formatBRLc(29)}/tri
                    </Button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="rounded-sm text-blaze hover:text-blaze" data-testid={`delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remover anúncio?</AlertDialogTitle>
                        <AlertDialogDescription>Esta ação não pode ser desfeita. O anúncio de {c.marca} {c.modelo} será excluído.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction className="bg-blaze hover:bg-obsidian" onClick={() => remover(c.id)} data-testid={`confirm-delete-${c.id}`}>Remover</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>

                {c.status === "sold" && (
                  <div className="mt-3 flex items-center gap-2 bg-secondary/60 rounded-sm px-3 py-2" data-testid={`visible-row-${c.id}`}>
                    <Switch checked={!!c.show_when_sold} onCheckedChange={(v) => toggleVisible(c, v)} data-testid={`visible-switch-${c.id}`} />
                    <span className="text-sm font-600">Manter na vitrine com selo VENDIDO <span className="text-muted-foreground font-400">(prova social)</span></span>
                  </div>
                )}

                {c.poster_paid && c.qr_active && c.cartaz_canceling && (
                  <div className="mt-3 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid={`cartaz-canceling-${c.id}`}>
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span className="text-sm font-600 text-amber-800 flex-1">
                      Assinatura do QR será cancelada. O QR continua ativo até {fmtDay(c.qr_valid_until)} e depois para de funcionar (o anúncio segue no ar).
                    </span>
                  </div>
                )}

                {c.poster_paid && !c.qr_active && (
                  <div className="mt-3 flex items-center gap-3 bg-blaze/5 border border-blaze/20 rounded-sm px-3 py-2" data-testid={`qr-off-note-${c.id}`}>
                    <QrCode className="w-4 h-4 text-blaze shrink-0" />
                    <span className="text-sm font-600 text-obsidian flex-1">
                      QR desativado: a assinatura não está ativa. Reative para o mesmo QR impresso voltar a funcionar — não precisa reimprimir nada.
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={scansOpen} onOpenChange={setScansOpen}>
        <DialogContent data-testid="scans-dialog">
          <DialogHeader>
            <DialogTitle>Scans do QR Code</DialogTitle>
            <DialogDescription>{scansCar ? `${scansCar.marca} ${scansCar.modelo}` : ""}</DialogDescription>
          </DialogHeader>
          {scans === null ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blaze" /></div>
          ) : (
            <div>
              <div className="flex items-center gap-3 bg-obsidian text-white rounded-sm p-4 mb-4">
                <ScanLine className="w-8 h-8 text-blaze" />
                <div>
                  <p className="font-heading font-900 text-3xl leading-none">{scans.count}</p>
                  <p className="text-xs text-white/60 mt-1">scans totais do QR físico</p>
                </div>
              </div>

              {scans.count > 0 && (
                <>
                  <div className="flex items-center gap-2 mb-2 text-sm font-700"><TrendingUp className="w-4 h-4 text-blaze" /> Scans por dia (últimos 14 dias)</div>
                  <div className="h-40 -ml-2 mb-4" data-testid="scans-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={scans.by_day}>
                        <XAxis dataKey="date" tickFormatter={(d) => d.slice(8) + "/" + d.slice(5, 7)} tick={{ fontSize: 10, fill: "#71717a" }} interval={1} axisLine={false} tickLine={false} />
                        <Tooltip cursor={{ fill: "rgba(255,59,48,0.08)" }} labelFormatter={(d) => `Dia ${d.slice(8)}/${d.slice(5, 7)}`} formatter={(v) => [v, "scans"]} contentStyle={{ borderRadius: 4, border: "1px solid #e4e4e7", fontSize: 12 }} />
                        <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                          {scans.by_day.map((e, i) => <Cell key={i} fill={e.count > 0 ? "#FF3B30" : "#e4e4e7"} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {(() => {
                    const best = (scans.by_hour || []).reduce((a, b) => (b.count > a.count ? b : a), { hour: 0, count: 0 });
                    return best.count > 0 ? (
                      <div className="flex items-center gap-2 bg-blaze/10 text-blaze rounded-sm px-3 py-2 mb-4 text-sm font-600" data-testid="best-hour">
                        <Clock className="w-4 h-4" /> Horário de maior interesse: <b>{String(best.hour).padStart(2, "0")}h–{String((best.hour + 1) % 24).padStart(2, "0")}h</b> ({best.count} scans)
                      </div>
                    ) : null;
                  })()}
                </>
              )}
              {scans.recent?.length ? (
                <div className="max-h-64 overflow-auto border border-border rounded-sm divide-y divide-border">
                  {scans.recent.map((s, i) => (
                    <div key={i} className="px-4 py-2.5 text-sm flex items-center justify-between" data-testid={`scan-row-${i}`}>
                      <span className="text-muted-foreground">Escaneado em</span>
                      <span className="font-600">{fmtDate(s.at)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">Ninguém escaneou o QR ainda. Imprima os materiais e divulgue o carro na rua!</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={soldOpen} onOpenChange={setSoldOpen}>
        <DialogContent data-testid="sold-dialog">
          <DialogHeader>
            <DialogTitle>Marcar como VENDIDO</DialogTitle>
            <DialogDescription>{soldCar ? `${soldCar.marca} ${soldCar.modelo}` : ""} — parabéns pela venda! 🎉</DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-3 py-2 cursor-pointer">
            <Checkbox checked={keepVisible} onCheckedChange={(v) => setKeepVisible(!!v)} data-testid="keep-visible-check" className="mt-0.5" />
            <span>
              <span className="font-700 block">Manter na vitrine com selo VENDIDO</span>
              <span className="text-sm text-muted-foreground">Ótimo como prova social — mostra aos compradores que seus carros vendem. Você pode desligar quando quiser.</span>
            </span>
          </label>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSoldOpen(false)}>Cancelar</Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white rounded-sm" onClick={() => setStatus(soldCar, "sold", keepVisible)} data-testid="confirm-sold-btn"><BadgeCheck className="w-4 h-4 mr-1" /> Confirmar venda</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={destaqueOpen} onOpenChange={setDestaqueOpen}>
        <DialogContent data-testid="destaque-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Rocket className="w-5 h-5 text-blaze" /> Destacar no topo</DialogTitle>
            <DialogDescription>{destaqueCar ? `${destaqueCar.marca} ${destaqueCar.modelo}` : ""} — assinatura que mantém seu anúncio no topo da vitrine, renovando automaticamente até você cancelar.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 py-2">
            {DESTAQUE_PLANS.map((p) => (
              <button key={p.plan} onClick={() => destaqueCar && destacarTopo(destaqueCar.id, p.plan)}
                className="relative border-2 border-border rounded-sm p-4 text-center hover:border-blaze hover:bg-blaze/5 transition-colors group"
                data-testid={`destaque-plan-${p.plan}`}>
                {p.tag && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-blaze text-white text-[9px] font-800 uppercase px-2 py-0.5 rounded-sm whitespace-nowrap">{p.tag}</span>}
                <p className="font-heading font-900 text-2xl text-obsidian">{p.label.split(" ")[0]}</p>
                <p className="text-xs text-muted-foreground">dias no topo</p>
                <p className="mt-3 font-800 text-blaze">{formatBRLc(p.price)}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{p.every}</p>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-center">Assinatura recorrente — renova sozinha até você cancelar no painel. Você será redirecionado ao checkout seguro.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
